import { Loader, Sidebar } from 'components';
import { useState, useRef } from 'react';
import {
  BankData,
  PersonalData,
  PersonalIdentification,
  ProfileSettings,
} from './'; // Adjust import path
import { useProfile } from 'hooks/useProfile'; // Adjust path
import { useSelector } from 'react-redux';
import { RootState } from 'data'; // Adjust path

interface FormSaveHandler {
  save: () => Promise<void>;
  hasChanges: () => boolean;
}

export const Profile = () => {
  const user = useProfile();
  const { loading: profileLoading } = useSelector(
    (state: RootState) => state.profile,
  );
  const [currentStep, setCurrentStep] = useState<string>('step1');
  const previousStepRef = useRef<string>('step1');
  
  // Refs to store save handlers for each step
  const step1SaveRef = useRef<FormSaveHandler | null>(null);
  const step2SaveRef = useRef<FormSaveHandler | null>(null);
  const step3SaveRef = useRef<FormSaveHandler | null>(null);
  const step4SaveRef = useRef<FormSaveHandler | null>(null);

  // Intercept step changes to auto-save before switching
  const handleStepChange = async (newStep: string) => {
    const previousStep = previousStepRef.current;
    
    // If switching to a different step, auto-save the current form if it has changes
    if (previousStep !== newStep) {
      let currentSaveHandler: FormSaveHandler | null = null;
      
      switch (previousStep) {
        case 'step1':
          currentSaveHandler = step1SaveRef.current;
          break;
        case 'step2':
          currentSaveHandler = step2SaveRef.current;
          break;
        case 'step3':
          currentSaveHandler = step3SaveRef.current;
          break;
        case 'step4':
          currentSaveHandler = step4SaveRef.current;
          break;
      }
      
      // Auto-save if form has changes
      if (currentSaveHandler && currentSaveHandler.hasChanges()) {
        try {
          await currentSaveHandler.save();
        } catch (error) {
          console.error('Error auto-saving form:', error);
        }
      }
    }
    
    previousStepRef.current = newStep;
    setCurrentStep(newStep);
  };

  const renderStepComponent = (step: string) => {
    switch (step) {
      case 'step1':
        return (
          <div className="flex items-stretch md:items-center p-[16px] md:p-[0px] flex-col gap-[24px] py-[20px] md:py-[112px] w-full">
            <ProfileSettings user={user} saveRef={step1SaveRef} />
          </div>
        );
      case 'step2':
        return (
          <div className="flex items-stretch md:items-center p-[16px] md:p-[0px] flex-col gap-[24px] py-[20px] md:py-[112px] w-full">
            <PersonalData user={user} saveRef={step2SaveRef} />
          </div>
        );
      case 'step3':
        return (
          <div className="flex items-stretch md:items-center p-[16px] md:p-[0px] flex-col gap-[24px] py-[20px] md:py-[112px] w-full">
            <PersonalIdentification user={user} saveRef={step3SaveRef} />
          </div>
        );
      case 'step4':
        return (
          <div className="flex items-stretch md:items-center p-[16px] md:p-[0px] flex-col gap-[24px] py-[20px] md:py-[112px] w-full">
            <BankData user={user} saveRef={step4SaveRef} />
          </div>
        );
      default:
        return <div>Select a step to view its content.</div>;
    }
  };

  return profileLoading || !user ? (
    <Loader />
  ) : (
    <div className="flex flex-col md:flex-row min-h-svh bg-gray-100 pt-header pb-tabbar">
      <div className="md:fixed z-10">
        <Sidebar renderStep={handleStepChange} currentStep={currentStep} />
      </div>
      <div className="flex-1 bg-gray-100 ml-0 md:ml-[272px]">
        {renderStepComponent(currentStep)}
      </div>
    </div>
  );
};
